import AlertTriangle from "assets/img/svg/alertTriangle";
import DropZoneCSV from "components/shared/UploadCsv";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TExampleData, TFileData } from "..";
import StepTitle from "../StepTitle";
import StepWrapper from "../StepWrapper";
import { formatDataFromFile, type ParsedCsvFile } from "../utils";
import styles from "./style.module.scss";

interface IUploadStep {
    setFileTitles: Dispatch<SetStateAction<string[]>>;
    setLoadedData: Dispatch<SetStateAction<TFileData | undefined>>;
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    /**
     * Sets columns matched.
     */
    setSelectedValues: Dispatch<SetStateAction<Record<string, string>>>;
    setFileName: Dispatch<SetStateAction<string>>;
    floatKeys: string[];
    /** Headers that cannot appear twice. Channel columns are not in this list. */
    singleValueHeaders?: readonly string[];
    setExampleData: Dispatch<SetStateAction<TExampleData | undefined>>;
}

/**
 * Step in BulkUpdate to upload file they're going to use.
 */
const UploadStep = ({
    setActiveStep,
    setFileTitles,
    setLoadedData,
    activeStep,
    setSelectedValues,
    setFileName,
    floatKeys,
    singleValueHeaders,
    setExampleData,
}: IUploadStep) => {
    const [t] = useTranslation();
    const [file, setFile] = useState([]);
    const [duplicateHeaders, setDuplicateHeaders] = useState<string[]>([]);
    const duplicateHeadersRef = useRef<string[]>([]);
    duplicateHeadersRef.current = duplicateHeaders;

    /**
     * By default, all columns are matched with "Ignore"
     */
    const initialSelectedValues = (fileTitles: string[]) => {
        const titles = {};
        fileTitles.forEach((item) => {
            titles[item] = "Ignore";
        });
        return titles;
    };

    const storeParsedBulkFile = (parsedFile: ParsedCsvFile) => {
        if (parsedFile.duplicateHeaders.length) {
            duplicateHeadersRef.current = parsedFile.duplicateHeaders;
            setDuplicateHeaders(parsedFile.duplicateHeaders);
            setLoadedData(undefined);
            setFileTitles([]);
            setSelectedValues({});
            return;
        }

        duplicateHeadersRef.current = [];
        setDuplicateHeaders([]);
        setLoadedData(parsedFile.loadedData as any);
        setFileTitles(parsedFile.fileTitles);
        setSelectedValues(initialSelectedValues(parsedFile.fileTitles));
    };

    useEffect(() => {
        if (!file.length) {
            duplicateHeadersRef.current = [];
            setDuplicateHeaders([]);
            return;
        }

        formatDataFromFile({ file: file[0] as File, floatKeys, singleValueHeaders }, storeParsedBulkFile);
        setFileName((file[0] as any).name ?? "");
    }, [file, floatKeys, singleValueHeaders]);

    const clearData = () => {
        setExampleData(undefined);
        setLoadedData(undefined);
        setSelectedValues({});
    };

    return (
        <StepWrapper
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            conditionToDisable={!file.length || duplicateHeaders.length > 0}
            clearData={clearData}
        >
            <div className={styles["update-step-container"]}>
                <StepTitle title={t("BulkUpdates.UploadStep.Title")} subtitle={t("BulkUpdates.UploadStep.Subtitle")} />
                <div className={styles["file-dropzone"]}>
                    <DropZoneCSV
                        setFile={setFile}
                        file={file}
                        nextStep={() => {
                            if (duplicateHeadersRef.current.length > 0) return;
                            setActiveStep((prev) => prev + 1);
                        }}
                        autoValidate
                        style={{ maxWidth: "600px", minWidth: "600px" }}
                    />
                    {duplicateHeaders.length > 0 && (
                        <div className={styles["header-error"]}>
                            <AlertTriangle />
                            <span>
                                {t("ImportData.Errors.DuplicateHeaders", {
                                    headers: duplicateHeaders.join(", "),
                                })}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </StepWrapper>
    );
};

export default UploadStep;
